import _ from 'lodash'
import { ActionContext, Store } from 'vuex'
import RootState from '../../state'
import ComponentState, { ColumnsData, IComponentData, IContainerData, isColumns, isContainer, RowsData, SectionData } from './state'
import { Direction, EAST, NORTH, SOUTH, WEST } from '../core/state'
import { Getters } from './helpers'
import { BatchUpdateRequest } from './mutations'
import ColumnSizes from '../../../misc/column-sizes'

type ComponentContext = ActionContext<ComponentState, RootState>
type VuexHandler = Store<RootState> | ComponentContext

export interface MovePoint {
  destination: IComponentData
  direction: Direction
}

interface InsertPoint {
  destination?: IComponentData
  direction: Direction
}

export default class ComponentBuilder {
  /**
   * @param {VuexHandler} handler - Vuex の Store にアクセスする手段
   */
  constructor(private handler: VuexHandler) {
  }

  /**
   * コンポーネントを削除するリクエストを生成する。
   * @param {IComponentData} data - 削除するもの
   * @returns {BatchUpdateRequest} - 更新リクエスト
   */
  remove(data: IComponentData): BatchUpdateRequest {
    return this.execute(data, (context, data) => {
      ComponentFactory.get(context, data).remove()
    })
  }

  /**
   * 記事のルート要素にコンポーネントを追加するリクエストを生成する。
   * @param {IComponentData} data - 追加するコンポーネント
   * @returns {BatchUpdateRequest} - 更新リクエスト
   */
  addToArticle(data: IComponentData): BatchUpdateRequest {
    return this.execute(data, (context, data) => {
      const article = ComponentFactory.getContainer(context, Getters.article(this.handler))
      article.insert(data, { direction: SOUTH })
    })
  }

  /**
   * コンポーネントを移動するリクエストを生成する。
   * @param {IComponentData} data - 移動するコンポーネント
   * @param {MovePoint} moveTo - 移動先
   * @returns {BatchUpdateRequest} - 更新リクエスト
   */
  move(data: IComponentData, moveTo: MovePoint): BatchUpdateRequest {
    return this.execute(data, (context, data) => {
      ComponentFactory.get(context, data).moveTo(moveTo)
    })
  }

  canMoveTo(data: IComponentData, moveTo: MovePoint): boolean {
    return ComponentFactory.get(new BuilderContext(this.handler), data).canMoveTo(moveTo)
  }

  /**
   * コンポーネントの更新を実行する。
   * @param {IComponentData} data - リクエストの起点となるコンポーネント
   * @param {(data: IComponentData, context: BuilderContext) => void} process - 処理内容
   * @returns {BatchUpdateRequest} - Mutation を更新するリクエスト情報
   */
  private execute(
    data: IComponentData,
    process: (context: BuilderContext, data: IComponentData) => void): BatchUpdateRequest {
    // data に破壊的変更を加える可能性があるため、 clone する
    const cloneData = _.cloneDeep(data)
    const context = new BuilderContext(this.handler)

    // process が処理した結果、 context に対し更新するべきコンポーネントが集積される
    process(context, cloneData)
    return context.createRequest()
  }
}

/**
 * コンポーネントを格納するコンテナ。
 */
class ComponentSet {
  private storage: { [id: string]: IComponentData }

  constructor() {
    this.storage = {}
  }

  get(id: string): IComponentData | undefined {
    return this.storage[id]
  }

  add(...component: IComponentData[]): void {
    component.forEach(c => this.storage[c.id] = c)
  }

  remove(...component: IComponentData[]): void {
    component.forEach(c => delete this.storage[c.id])
  }

  contains(component: IComponentData): boolean {
    return !!this.storage[component.id]
  }

  toArray(): IComponentData[] {
    return _.map(this.storage, (component, id) => component)
  }
}

function stringify(data?: IComponentData): string {
  if (!data) {
    return '{undefined}'
  }
  if (isContainer(data)) {
    return `{id=${data.id}, type=${data.type}, parentId=${data.parentId}, childIds=[${data.childIds.join(',')}]}`
  }
  return `{id=${data.id}, type=${data.type}, parentId=${data.parentId}}`
}

class BuilderContext {
  private cache = new ComponentSet()
  private updateSet = new ComponentSet()
  private removeSet = new ComponentSet()

  /**
   * @param {VuexHandler} handler - Vuex の Store にアクセスする手段
   */
  constructor(private handler: VuexHandler) {
  }

  generateId(): string {
    return Getters.idGenerator(this.handler)()
  }

  getComponent(id: string): IComponent
  getComponent(data: IComponentData): IComponent
  getComponent(componentOrId: string | IComponentData): IComponent {
    const data = this.getComponentData(componentOrId)
    return ComponentFactory.get(this, data)
  }

  getContainer(id: string): IContainer
  getContainer(data: IContainerData): IContainer
  getContainer(componentOrId: string | IContainerData): IContainer {
    const data = this.getContainerData(componentOrId)
    return ComponentFactory.getContainer(this, data)
  }

  getParentIfExists(child: IComponentData): IContainer | undefined {
    const parentId = child.parentId
    return parentId ? this.getContainer(parentId) : undefined
  }

  getParent(child: IComponentData): IContainer {
    const parent = this.getParentIfExists(child)
    if (parent) {
      return parent
    }
    throw new Error(`親コンテナが存在しません: ${stringify(child)}`)
  }

  update(...component: IComponentData[]): this {
    this.updateSet.add(...component)
    this.removeSet.remove(...component)
    return this
  }

  remove(...component: IComponentData[]): this {
    this.removeSet.add(...component)
    this.updateSet.remove(...component)
    return this
  }

  createRequest(): BatchUpdateRequest {
    return {
      update: this.updateSet.toArray(),
      remove: this.removeSet.toArray()
    }
  }

  private getComponentData(componentOrId: string | IComponentData): IComponentData {
    const id = _.isString(componentOrId) ? componentOrId : componentOrId.id

    // キャッシュを通じて、 ID 1つに対する IComponentData インスタンスが常に 1つであることを保証する。
    // そうすることで、 IComponent インスタンスを複数作った場合でもデータの整合性が守れる。
    const fromCache = this.cache.get(id)
    if (fromCache) {
      return fromCache
    }
    const component = _.cloneDeep(_.isString(componentOrId) ?
      Getters.componentFinder(this.handler)(componentOrId) : componentOrId)
    this.cache.add(component)
    return component
  }

  private getContainerData(componentOrId: string | IContainerData): IContainerData {
    const component = this.getComponentData(componentOrId)
    if (isContainer(component)) {
      return component
    }
    throw new TypeError(`コンポーネントはコンテナではありません: ${stringify(component)}`)
  }
}

interface IComponent<T extends IComponentData = IComponentData> {
  readonly data: T

  /**
   * このコンポーネントを移動する。
   * @param {MovePoint} moveTo - 移動先
   */
  moveTo(moveTo: MovePoint): void

  /**
   * このコンポーネントを移動できる場合、 true を返す。
   * @param {MovePoint} moveTo - 移動先
   * @return {boolean} - 移動できる場合、 true
   */
  canMoveTo(moveTo: MovePoint): boolean

  /**
   * このコンポーネントを削除する。
   */
  remove(): void

  /**
   * @returns {this is IContainer} - このコンポーネントがコンテナである場合、 true
   */
  isContainer(): this is IContainer
}

interface IContainer<T extends IContainerData = IContainerData> extends IComponent<T> {
  readonly data: T

  /**
   * このコンポーネントに別のコンポーネントを追加する。
   * @param {IComponentData} data - 追加するコンポーネント
   * @param {InsertPoint} insertTo - 追加する位置
   */
  insert(data: IComponentData, insertTo: InsertPoint): void

  /**
   * このコンポーネントに別のコンポーネントを追加できる場合、 true を返す。
   * @param {IComponentData} data - 追加するコンポーネント
   * @param {InsertPoint} insertTo - 追加する位置
   * @return {boolean} 追加できる場合、 true
   */
  canInsertTo(data: IComponentData, insertTo: InsertPoint): boolean

  /**
   * 子のコンポーネントを削除する
   * @param {IComponentData} data - 削除する子
   */
  removeChild(data: IComponentData): void
}

abstract class Component<T extends IComponentData = IComponentData> implements IComponent<T> {
  constructor(protected readonly context: BuilderContext, public readonly data: T) {
  }

  abstract moveTo(moveTo: MovePoint): void

  abstract canMoveTo(moveTo: MovePoint): boolean

  remove(): void {
    const parent = this.context.getParentIfExists(this.data)
    if (parent) {
      parent.removeChild(this.data)
    }
    this.context.remove(this.data)
  }

  isContainer(): this is IContainer {
    return false
  }
}

abstract class Container<T extends IContainerData = IContainerData> extends Component<T> implements IContainer<T> {
  constructor(context: BuilderContext, public readonly data: T) {
    super(context, data)
  }

  abstract canInsertTo(data: IComponentData, insertTo: InsertPoint): boolean

  insert(data: IComponentData, insertTo: InsertPoint): void {
    const previousParent = this.context.getParentIfExists(data)
    if (previousParent) {
      previousParent.removeChild(data)
    }
    const index = this.calcInsertionIndex(insertTo)
    this.insertChildTo(data, index)
    data.parentId = this.data.id
    this.context.update(this.data, data)
  }

  removeChild(data: IComponentData): void {
    const index = this.data.childIds.indexOf(data.id)
    if (index !== -1) {
      this.removeChildAt(index)
      this.context.update(this.data)
    }
  }

  isContainer(): this is IContainer {
    return true
  }

  /**
   * コンポーネントが自分の子であれば true を返す
   * @param {IComponentData} data - コンポーネント
   * @return {boolean} - data が自分の子であれば true
   */
  contains(data: IComponentData): boolean {
    return this.data.childIds.indexOf(data.id) >= 0
  }

  /**
   * コンポーネントの挿入位置を計算する。
   */
  protected calcInsertionIndex(insertTo: InsertPoint): number {
    if (this.data.childIds.length === 0) {
      return 0
    }
    const direction = insertTo.direction
    const isHead = this.isHead(direction)
    const childIds = this.data.childIds
    const destination = insertTo.destination
    const baseIndex = (destination) ? childIds.indexOf(destination.id) : -1
    if (baseIndex < 0) {
      // 基準となるコンポーネントが不明 or 未指定の場合、先頭か末尾に追加する
      return isHead ? 0 : childIds.length
    }
    // 基準となるコンポーネントの前後どちらかに追加する
    return baseIndex + (isHead ? 0 : 1)
  }

  /**
   * @param {Direction} direction - 方角
   * @returns {boolean} - 方角が先頭である場合 true, 後ろである場合 false
   */
  protected isHead(direction: Direction): boolean {
    return (direction === NORTH || direction === WEST)
  }

  /**
   * @param {Direction} direction - 方角
   * @returns {boolean} - 方角が列方向である場合 true, 行方向である場合 false
   */
  protected isColumnDirection(direction: Direction): boolean {
    return (direction === WEST || direction === EAST)
  }

  /**
   * 指定された子を行コンテナ、または列コンテナでラップし、そこに data を追加する。
   * @param {IComponentData} data - 追加するコンポーネント
   * @param {IComponentData} childToBeWrapped - 追加位置にある子。これをコンテナでラップする
   * @param {Direction} direction - 追加する方角
   */
  protected wrapWithContainer(data: IComponentData, childToBeWrapped: IComponentData, direction: Direction): void {
    const childIndex = this.data.childIds.indexOf(childToBeWrapped.id)
    if (childIndex === -1) {
      throw new Error(`挿入位置には子のコンポーネントを指定してください: this=${stringify(this.data)}`
        + `childToBeWrapped=${stringify(childToBeWrapped)}`)
    }

    const containerId = this.context.generateId()

    this.data.childIds[childIndex] = containerId
    this.context.update(this.data)

    const child = this.context.getComponent(childToBeWrapped)
    child.remove()
    child.data.parentId = containerId
    this.context.update(child.data)

    const containerData: IContainerData = this.isColumnDirection(direction) ?
      new ColumnsData(containerId) : new RowsData(containerId)
    containerData.parentId = this.data.id
    containerData.childIds = [child.data.id]

    this.context.getContainer(containerData)
      .insert(data, { direction, destination: child.data })
  }

  protected insertChildTo(data: IComponentData, index: number): void {
    this.data.childIds.splice(index, 0, data.id)
  }

  protected removeChildAt(index: number): void {
    this.data.childIds.splice(index, 1)
  }
}

class Article extends Container {
  constructor(context: BuilderContext, data: IContainerData) {
    super(context, data)
  }

  moveTo(moveTo: MovePoint): void {
    throw new TypeError(`ルート要素である Article は移動できません: ${stringify(this.data)}`)
  }

  canMoveTo(moveTo: MovePoint): boolean {
    return false
  }

  insert(data: IComponentData, insertTo: InsertPoint): void {
    if (data.type === 'section') {
      super.insert(data, insertTo)
      return
    }
    // Article 直下は Section 以外を追加できない。
    // 追加するものが Section ではない場合、 Section でラップする
    const section = new SectionData(this.context.generateId())
    section.childIds = [data.id]
    super.insert(section, insertTo)

    this.context.getComponent(data).remove()

    data.parentId = section.id
    this.context.update(data)
  }

  canInsertTo(data: IComponentData, insertTo: InsertPoint): boolean {
    return true
  }
}

class Section extends Container {
  constructor(context: BuilderContext, data: IContainerData) {
    super(context, data)
  }

  insert(data: IComponentData, insertTo: InsertPoint): void {
    const child = this.getChild()
    if (!child) {
      // 唯一の子として追加する
      super.insert(data, insertTo)
      return
    }
    if (this.tryMergeToChild(data, child, insertTo)) {
      return
    }
    this.wrapWithContainer(data, child.data, insertTo.direction)
  }

  canInsertTo(data: IComponentData, insertTo: InsertPoint): boolean {
    const child = this.getChild()
    if (!child) {
      return true
    }
    if (child.isContainer()) {
      return child.canInsertTo(data, insertTo)
    }
    return true
  }

  moveTo(moveTo: MovePoint): void {
    const parent = this.context.getParent(moveTo.destination)
    if (parent.data.type !== 'article') {
      throw new Error(`Section は Article 直下にしか移動できません: this=${stringify(this.data)}, `
        + `, moveTo=${stringify(moveTo.destination)}, parent=${stringify(parent.data)}`)
    }
    parent.insert(this.data, moveTo)
  }

  canMoveTo(moveTo: MovePoint): boolean {
    const parent = this.context.getParentIfExists(moveTo.destination)
    // 親がないコンテナ、つまり Article には無条件で追加できる
    return !parent || parent.data.type === 'article'
  }

  /**
   * Section の子（1つしか存在しない）を取得する。
   * @returns {IComponent}
   */
  private getChild(): IComponent | undefined {
    if (this.data.childIds.length === 0) {
      return undefined
    }
    return this.context.getComponent(this.data.childIds[0])
  }

  /**
   * 子のコンテナにマージしようと試みる。
   * @param {IComponentData} data - 追加するもの
   * @param {IComponent} child - Section 唯一の子
   * @param {InsertPoint} insertTo - 追加する位置
   * @returns {boolean} - 子にマージできた場合、 true
   */
  private tryMergeToChild(data: IComponentData, child: IComponent, insertTo: InsertPoint): boolean {
    if (!child.isContainer()) {
      return false
    }
    const isColumnDirection = this.isColumnDirection(insertTo.direction)
    if ((child.data.type === 'columns' && isColumnDirection) ||
      (child.data.type === 'rows' && !isColumnDirection)) {
      // 行、列の先頭または末尾に、コンポーネントを追加する
      child.insert(data, { direction: insertTo.direction })
      return true
    }
    return false
  }
}

abstract class SingleDirectionContainer<T extends IContainerData = IContainerData> extends Container<T> {
  constructor(context: BuilderContext, data: T) {
    super(context, data)
  }

  /**
   * @param {Direction} direction - 方角
   * @returns {boolean} - 指定された方角を扱うコンテナであれば true
   */
  abstract isDirectionAcceptable(direction: Direction): boolean

  insert(data: IComponentData, insertTo: InsertPoint): void {
    if (this.isDirectionAcceptable(insertTo.direction) || this.data.childIds.length === 0 || !insertTo.destination) {
      super.insert(data, insertTo)
      return
    }
    this.wrapWithContainer(data, insertTo.destination, insertTo.direction)
  }

  canInsertTo(data: IComponentData, insertTo: InsertPoint): boolean {
    return true
  }

  moveTo(moveTo: MovePoint): void {
    throw new Error(`移動はサポートしません: ${stringify(this.data)}`)
  }

  canMoveTo(moveTo: MovePoint): boolean {
    return false
  }
}

class Rows extends SingleDirectionContainer {
  constructor(context: BuilderContext, data: IContainerData) {
    super(context, data)
  }

  isDirectionAcceptable(direction: Direction): boolean {
    return !this.isColumnDirection(direction)
  }
}

class Columns extends SingleDirectionContainer<ColumnsData> {

  constructor(context: BuilderContext, data: ColumnsData) {
    super(context, data)
  }

  isDirectionAcceptable(direction: Direction): boolean {
    return this.isColumnDirection(direction)
  }

  canInsertTo(data: IComponentData, insertTo: InsertPoint): boolean {
    if (this.isColumnDirection(insertTo.direction) && this.data.childIds.length >= ColumnsData.MAX_COLUMN_SIZE) {
      return this.contains(data)
    }
    return super.canInsertTo(data, insertTo)
  }

  insertChildTo(data: IComponentData, index: number): void {
    const columnSizes: ColumnSizes = !!this.data.itemSizes && this.data.itemSizes.length === this.data.childIds.length
      ? new ColumnSizes(this.data.itemSizes)
      : ColumnSizes.ofNaturalSizes(ColumnsData.MAX_COLUMN_SIZE, this.data.childIds.length)
    this.data.itemSizes = columnSizes.insertItemAt(index).itemSizes
    super.insertChildTo(data, index)
  }

  removeChildAt(index: number): void {
    const columnSizes: ColumnSizes = !!this.data.itemSizes && this.data.itemSizes.length === this.data.childIds.length
      ? new ColumnSizes(this.data.itemSizes)
      : ColumnSizes.ofNaturalSizes(ColumnsData.MAX_COLUMN_SIZE, this.data.childIds.length)
    this.data.itemSizes = columnSizes.removeItemAt(index).itemSizes
    super.removeChildAt(index)
  }
}

/**
 * 子を持たないコンポーネント
 */
class LeafComponent extends Component {
  constructor(context: BuilderContext, data: IComponentData) {
    super(context, data)
  }

  moveTo(moveTo: MovePoint): void {
    this.context.getParent(moveTo.destination).insert(this.data, moveTo)
  }

  canMoveTo(moveTo: MovePoint): boolean {
    const parent = this.context.getParentIfExists(moveTo.destination)
    // 親がないコンテナ、つまり Article には無条件で追加できる
    return !parent || this.context.getParent(moveTo.destination).canInsertTo(this.data, moveTo)
  }
}

class ComponentFactory {
  constructor() {
  }

  public static get(context: BuilderContext, data: IComponentData): IComponent {
    const type = data.type
    if (['text', 'image', 'embed', 'url'].indexOf(type) >= 0) {
      return new LeafComponent(context, data)
    }
    if (!isContainer(data)) {
      throw new TypeError(`コンポーネントの種類が不明です: ${stringify(data)}`)
    }
    return ComponentFactory.getContainer(context, data)
  }

  public static getContainer(context: BuilderContext, data: IContainerData): IContainer {
    if (data.type === 'article') {
      return new Article(context, data)
    }
    if (data.type === 'section') {
      return new Section(context, data)
    }
    if (data.type === 'rows') {
      return new Rows(context, data)
    }
    if (isColumns(data)) {
      return new Columns(context, data)
    }
    throw new TypeError(`コンポーネントの種類が不明です: ${stringify(data)}`)
  }
}
